import { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import { Save, Eye, EyeOff } from 'lucide-react';

export function AdminSettings() {
  const [discountsEnabled, setDiscountsEnabled] = useState(true);
  const [eventsEnabled, setEventsEnabled] = useState(true);
  const [jobsEnabled, setJobsEnabled] = useState(true);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const { data, error } = await supabase
        .from('app_settings')
        .select('discounts_enabled, events_enabled, jobs_enabled')
        .single();

      if (error) throw error;

      if (data) {
        setDiscountsEnabled(data.discounts_enabled);
        setEventsEnabled(data.events_enabled);
        setJobsEnabled(data.jobs_enabled);
      }
    } catch (error) {
      console.error('Error fetching settings:', error);
      setMessage({ type: 'error', text: 'Failed to load settings' });
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setMessage(null);

    try {
      const { data: existingSettings } = await supabase
        .from('app_settings')
        .select('id')
        .single();

      const { error } = await supabase
        .from('app_settings')
        .update({
          discounts_enabled: discountsEnabled,
          events_enabled: eventsEnabled,
          jobs_enabled: jobsEnabled,
        })
        .eq('id', existingSettings?.id);

      if (error) throw error;

      setMessage({ type: 'success', text: 'Settings saved successfully!' });
      setTimeout(() => setMessage(null), 3000);
    } catch (error) {
      console.error('Error saving settings:', error);
      setMessage({ type: 'error', text: 'Failed to save settings' });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">App Settings</h1>
        <p className="text-gray-600">
          Control which features are visible to all users. When disabled, tabs will be hidden from navigation.
        </p>
      </div>

      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-6">Feature Visibility</h2>

          <div className="space-y-6">
            <ToggleSetting
              label="Discounts"
              description="Show or hide the Discounts tab and page for all users"
              enabled={discountsEnabled}
              onChange={setDiscountsEnabled}
            />

            <ToggleSetting
              label="Events"
              description="Show or hide the Events tab and page for all users"
              enabled={eventsEnabled}
              onChange={setEventsEnabled}
            />

            <ToggleSetting
              label="Jobs"
              description="Show or hide the Jobs tab and page for all users"
              enabled={jobsEnabled}
              onChange={setJobsEnabled}
            />
          </div>
        </div>

        <div className="bg-gray-50 px-6 py-4 border-t border-gray-200 flex items-center justify-between">
          <div className="flex-1">
            {message && (
              <div
                className={`text-sm font-medium ${
                  message.type === 'success' ? 'text-green-600' : 'text-red-600'
                }`}
              >
                {message.text}
              </div>
            )}
          </div>
          <button
            onClick={handleSave}
            disabled={saving}
            className="inline-flex items-center px-6 py-2.5 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Save size={18} className="mr-2" />
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>

      <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
        <h3 className="text-sm font-semibold text-blue-900 mb-2">Note</h3>
        <p className="text-sm text-blue-800">
          Changes take effect immediately for all users. Users currently viewing a disabled page will
          see it until they navigate away or refresh.
        </p>
      </div>
    </div>
  );
}

interface ToggleSettingProps {
  label: string;
  description: string;
  enabled: boolean;
  onChange: (enabled: boolean) => void;
}

function ToggleSetting({ label, description, enabled, onChange }: ToggleSettingProps) {
  return (
    <div className="flex items-start justify-between py-4 border-b border-gray-100 last:border-0">
      <div className="flex-1">
        <div className="flex items-center gap-2 mb-1">
          <h3 className="text-base font-semibold text-gray-900">{label}</h3>
          {enabled ? (
            <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
              <Eye size={12} />
              Visible
            </span>
          ) : (
            <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
              <EyeOff size={12} />
              Hidden
            </span>
          )}
        </div>
        <p className="text-sm text-gray-600">{description}</p>
      </div>
      <button
        onClick={() => onChange(!enabled)}
        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ml-4 flex-shrink-0 ${
          enabled ? 'bg-blue-600' : 'bg-gray-200'
        }`}
      >
        <span
          className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
            enabled ? 'translate-x-6' : 'translate-x-1'
          }`}
        />
      </button>
    </div>
  );
}
